import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Permission } from "./roles";
import { Toastify } from "../../../components/Toaster";

export interface PermissionsState {
  Data: Permission[];
  isLoading: boolean;
  isAdding: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  isSuccess: boolean;
  isError: boolean;
  errorMessage: string | null;
}

interface AllPermissionAPIResponse {
  ErrorCode?: string;
  Message?: string;
  Data: Permission[];
}

interface PermissionAPIResponse {
  ErrorCode?: string;
  Message?: string;
  Data: Permission;
}

const initialState: PermissionsState = {
  Data: [],
  isLoading: false,
  isAdding: false,
  isUpdating: false,
  isDeleting: false,
  isSuccess: false,
  isError: false,
  errorMessage: null
};

// Slice
export const PermissionsSlice = createSlice({
  name: "permissions",
  initialState,
  reducers: {
    getPermissionsRequest(state) {
      state.isLoading = true;
      state.isSuccess = false;
      state.isAdding = false;
      state.isUpdating = false;
      state.isDeleting = false;
      state.isError = false;
      state.errorMessage = null;
    },
    getPermissionsSuccess(state, action: PayloadAction<AllPermissionAPIResponse>) {
      state.isLoading = false;
      state.Data = action.payload.Data;
      state.isSuccess = true;
    },
    getPermissionsError(state, action: PayloadAction<string>) {
      state.isLoading = false;
      state.isError = true;
      state.errorMessage = action.payload;
      Toastify("error", action.payload);
    },

    createPermissionRequest(state, _: PayloadAction<Permission>) {
      state.isAdding = true;
      state.isSuccess = false;
      state.isError = false;
      state.errorMessage = null;
    },
    createPermissionSuccess(state, action: PayloadAction<PermissionAPIResponse>) {
      state.isAdding = false;
      state.isSuccess = true;
      state.Data.push(action.payload.Data);
      Toastify("success", "Permission created successfully");
    },
    createPermissionError(state, action: PayloadAction<string>) {
      state.isAdding = false;
      state.isError = true;
      state.errorMessage = action.payload;
      Toastify("error", action.payload);
    },

    updatePermissionRequest(state, _: PayloadAction<Permission>) {
      state.isUpdating = true;
      state.isSuccess = false;
      state.isError = false;
      state.errorMessage = null;
    },
    updatePermissionSuccess(state, action: PayloadAction<PermissionAPIResponse>) {
      state.isUpdating = false;
      state.isSuccess = true;
      const index = state.Data.findIndex((permission) => permission.Id === action.payload.Data.Id);
      state.Data[index] = action.payload.Data;
      Toastify("success", "Permission updated successfully");
    },
    updatePermissionError(state, action: PayloadAction<string>) {
      state.isUpdating = false;
      state.isError = true;
      state.errorMessage = action.payload;
      Toastify("error", action.payload);
    },

    deletePermissionRequest(state, _: PayloadAction<string>) {
      state.isDeleting = true;
      state.isSuccess = false;
      state.isError = false;
      state.errorMessage = null;
    },

    deletePermissionSuccess(state, action: PayloadAction<string>) {
      state.isDeleting = false;
      state.isSuccess = true;
      state.Data = state.Data.filter((permission) => permission.Id !== action.payload);
      Toastify("success", "Permission deleted successfully");
    },
    deletePermissionError(state, action: PayloadAction<string>) {
      state.isDeleting = false;
      state.isError = true;
      state.errorMessage = action.payload;
      Toastify("error", action.payload);
    }
  }
});

export const {
  getPermissionsRequest,
  getPermissionsSuccess,
  getPermissionsError,
  createPermissionRequest,
  createPermissionSuccess,
  createPermissionError,
  updatePermissionRequest,
  updatePermissionSuccess,
  updatePermissionError,
  deletePermissionRequest,
  deletePermissionSuccess,
  deletePermissionError
} = PermissionsSlice.actions;

export default PermissionsSlice.reducer;
